import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { toast } from "sonner";
import {
  useGetAcademyQuery,
  useCreateUserMutation,
  useGetDepartmentQuery,
  useCreateDepartmentMutation,
  useGetUsersQuery,
  useGetRolesQuery,
  useSoftDeleteUserMutation,
  useDeleteDepartmentMutation,
  useUpdateDepartmentMutation,
  useGetDivisionsQuery,
  useCreateDivisionMutation,
  useUpdateDivisionMutation,
  useDeleteDivisionMutation,
  useGetUserByEmailQuery,
} from "../slices/userApiSlice";
import { ROLE_NAMES, isValidEmail } from "../utils/userManagementUtils";

// Selectors
const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

export const useUserManagement = () => {
  // ========== State Management ==========
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("Department");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState({ column: null, ascending: true });

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [showModalDepartment, setShowModalDepartment] = useState(false);
  const [showModalDivision, setShowModalDivision] = useState(false);
  const [showEditDepartmentModal, setShowEditDepartmentModal] = useState(false);

  // User Form State
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    employeeId: "",
  });
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedUserDivision, setSelectedUserDivision] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [originalUserData, setOriginalUserData] = useState({
    name: "",
    email: "",
    employeeId: "",
    departmentId: null,
    divisionId: null,
  });

  // Department Form State
  const [departmentForm, setDepartmentForm] = useState({
    name: "",
    description: "",
  });
  const [editDepartmentData, setEditDepartmentData] = useState({
    name: "",
    description: "",
  });
  const [originalDepartmentData, setOriginalDepartmentData] = useState({
    name: "",
    description: "",
  });
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  // Division Form State
  const [divisionForm, setDivisionForm] = useState({ name: "" });
  const [isEditingDivision, setIsEditingDivision] = useState(false);
  const [editDivisionId, setEditDivisionId] = useState(null);

  // Error State
  const [formError, setFormError] = useState(null);
  const [emailError, setEmailError] = useState("");

  // Data State
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [roleIds, setRoleIds] = useState({
    manager: null,
    technician: null,
    supervisor: null,
  });

  // ========== API Hooks ==========
  const userEmail = useSelector(getUserEmail);
  const { data: userData } = useGetUserByEmailQuery(userEmail);

  const { data: academies, isLoading: isLoadingAcademies } =
    useGetAcademyQuery();

  const {
    data: departmentData,
    refetch: refetchDepartments,
    isLoading: isLoadingDepartments,
  } = useGetDepartmentQuery();

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = useGetUsersQuery();

  const { data: roles, isLoading: isLoadingRoles } = useGetRolesQuery();

  const {
    data: divisions,
    refetch: refetchDivisions,
    isLoading: isLoadingDivisions,
  } = useGetDivisionsQuery();

  // Mutations
  const [createUser, { isLoading: isCreatingUser }] = useCreateUserMutation();
  const [createDepartment] = useCreateDepartmentMutation();
  const [updateDepartment, { isLoading: isUpdatingDepartment }] =
    useUpdateDepartmentMutation();
  const [deleteDepartment] = useDeleteDepartmentMutation();
  const [createDivision] = useCreateDivisionMutation();
  const [updateDivision, { isLoading: isUpdatingDivision }] =
    useUpdateDivisionMutation();
  const [deleteDivision] = useDeleteDivisionMutation();
  const [softDeleteUser] = useSoftDeleteUserMutation();

  // ========== Effects ==========
  // Show loading indicator when fetching data
  useEffect(() => {
    const isLoading =
      isLoadingUsers ||
      isLoadingAcademies ||
      isLoadingDepartments ||
      isLoadingRoles;

    if (isLoading) {
      toast.loading("Loading data...", { id: "data-loading" });
    } else {
      toast.dismiss("data-loading");
    }
    return () => toast.dismiss("data-loading");
  }, [
    isLoadingUsers,
    isLoadingAcademies,
    isLoadingDepartments,
    isLoadingRoles,
  ]);

  // Extract role IDs from roles data
  useEffect(() => {
    if (roles && Array.isArray(roles)) {
      const newRoleIds = { manager: null, technician: null, supervisor: null };

      roles.forEach((role) => {
        const roleName = role?.name?.toLowerCase();
        // Extract ID as string, handling both roleId and _id
        const id = role?.roleId || role?._id;
        const roleId = id ? (typeof id === 'string' ? id : id.$oid || id.toString()) : null;

        if (roleName === ROLE_NAMES.MANAGER) {
          newRoleIds.manager = roleId;
        } else if (roleName === ROLE_NAMES.TECHNICIAN) {
          newRoleIds.technician = roleId;
        } else if (roleName === ROLE_NAMES.SUPERVISOR) {
          newRoleIds.supervisor = roleId;
        }
      });

      setRoleIds(newRoleIds);
    }
  }, [roles]);

  // Sync departments with fetched data
  useEffect(() => {
    if (departmentData && Array.isArray(departmentData)) {
      setDepartments(departmentData);
    }
  }, [departmentData]);

  // Sync users with fetched data
  useEffect(() => {
    if (usersData) {
      setUsers(usersData);
    }
  }, [usersData]);

  // Reset division selection when department changes (but not when editing)
  useEffect(() => {
    if (!isEditingUser) {
      setSelectedUserDivision(null);
    }
  }, [selectedDepartment, isEditingUser]);

  // Set original data when editing starts
  useEffect(() => {
    if (isEditingUser && showModal) {
      // This will be called after the modal opens and form is populated
      const originalData = {
        name: userForm.name,
        email: userForm.email,
        employeeId: userForm.employeeId,
        password: "",
        departmentId: selectedDepartment?.value || null,
        divisionId: selectedUserDivision?.value || null,
      };
      setOriginalUserData(originalData);
    }
  }, [
    isEditingUser,
    showModal,
    userForm,
    selectedDepartment,
    selectedUserDivision,
  ]);

  // ========== Computed Values ==========
  const academyId = userData?.user?.academyId;

  const filteredDivisionsForUser = useMemo(() => {
    return (
      divisions?.filter(
        (division) => division.departmentId === selectedDepartment?.value,
      ) || []
    );
  }, [divisions, selectedDepartment]);

  const departmentOptions = useMemo(() => {
    return (
      departments?.map((d) => ({
        value: d.departmentId || d._id,
        label: d.name,
      })) || []
    );
  }, [departments]);

  const divisionOptions = useMemo(() => {
    let options =
      filteredDivisionsForUser?.map((d) => ({
        value: d.divisionId || d._id,
        label: d.name || d.divisionName,
      })) || [];

    // If editing a user and they have a division that's not in the filtered options,
    // add it to the options so it can be displayed
    if (
      isEditingUser &&
      selectedUserDivision &&
      !options.find((opt) => opt.value === selectedUserDivision.value)
    ) {
      options = [selectedUserDivision, ...options];
    }

    return options;
  }, [filteredDivisionsForUser, isEditingUser, selectedUserDivision]);

  // ========== Helper Functions ==========
  const getRoleIdForTab = useCallback(() => {
    switch (activeTab) {
      case "Manager":
        return roleIds.manager;
      case "Supervisor":
        return roleIds.supervisor;
      case "Technician":
        return roleIds.technician;
      default:
        return null;
    }
  }, [activeTab, roleIds]);

  const showSuccessToast = useCallback((message) => {
    toast.success(message);
  }, []);

  const showErrorAlert = useCallback((message) => {
    toast.error("Error", {
      description: message,
    });
  }, []);

  const showConfirmDialog = useCallback(async (title, text) => {
    return new Promise((resolve) => {
      const { dismiss } = toast(title, {
        description: text,
        duration: Infinity,
        action: {
          label: "Confirm",
          onClick: () => {
            resolve({ isConfirmed: true });
            dismiss();
          },
        },
        cancel: {
          label: "Cancel",
          onClick: () => {
            resolve({ isConfirmed: false });
            dismiss();
          },
        },
      });
    });
  }, []);

  // ========== Form Handlers ==========
  const resetUserForm = useCallback(() => {
    setUserForm({
      name: "",
      email: "",
      password: "",
      employeeId: "",
    });
    setSelectedDepartment(null);
    setSelectedUserDivision(null);
    setEmailError("");
    setFormError(null);
    setIsEditingUser(false);
    setSelectedUserId(null);
    setOriginalUserData({
      name: "",
      email: "",
      employeeId: "",
      password: "",
      departmentId: null,
      divisionId: null,
    });
  }, []);

  const resetDepartmentForm = useCallback(() => {
    setDepartmentForm({
      name: "",
      description: "",
    });
  }, []);

  const resetDivisionForm = useCallback(() => {
    setDivisionForm({ name: "" });
    setSelectedDepartment(null);
    setIsEditingDivision(false);
    setEditDivisionId(null);
    setFormError(null);
  }, []);

  const handleUserFormChange = useCallback((field, value) => {
    setUserForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleEmailChange = useCallback(
    (value) => {
      handleUserFormChange("email", value);
      if (!isValidEmail(value)) {
        setEmailError("Invalid email format");
      } else {
        setEmailError("");
      }
    },
    [handleUserFormChange],
  );

  // ========== Change Detection ==========
  const hasDepartmentChanges = useMemo(() => {
    return (
      editDepartmentData.name !== originalDepartmentData.name ||
      editDepartmentData.description !== originalDepartmentData.description
    );
  }, [editDepartmentData, originalDepartmentData]);

  const hasUserChanges = useMemo(() => {
    // Only consider password changes if password is actually filled and different from original
    const hasPasswordChange =
      userForm.password &&
      userForm.password.trim() !== "" &&
      userForm.password !== originalUserData.password;

    const hasChanges =
      userForm.name !== originalUserData.name ||
      userForm.email !== originalUserData.email ||
      userForm.employeeId !== originalUserData.employeeId ||
      selectedDepartment?.value !== originalUserData.departmentId ||
      selectedUserDivision?.value !== originalUserData.divisionId ||
      hasPasswordChange;

    // Debug logging
    console.log("Change detection debug:", {
      currentForm: userForm,
      originalData: originalUserData,
      selectedDepartment: selectedDepartment?.value,
      originalDepartment: originalUserData.departmentId,
      selectedDivision: selectedUserDivision?.value,
      originalDivision: originalUserData.divisionId,
      hasPasswordChange,
      hasChanges,
    });

    return hasChanges;
  }, [userForm, selectedDepartment, selectedUserDivision, originalUserData]);

  return {
    // State
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    currentPage,
    setCurrentPage,
    sortOrder,
    setSortOrder,
    showModal,
    setShowModal,
    showModalDepartment,
    setShowModalDepartment,
    showModalDivision,
    setShowModalDivision,
    showEditDepartmentModal,
    setShowEditDepartmentModal,
    userForm,
    setUserForm,
    selectedDepartment,
    setSelectedDepartment,
    selectedUserDivision,
    setSelectedUserDivision,
    selectedUserId,
    setSelectedUserId,
    isEditingUser,
    setIsEditingUser,
    originalUserData,
    setOriginalUserData,
    departmentForm,
    setDepartmentForm,
    editDepartmentData,
    setEditDepartmentData,
    originalDepartmentData,
    setOriginalDepartmentData,
    selectedDepartmentId,
    setSelectedDepartmentId,
    divisionForm,
    setDivisionForm,
    isEditingDivision,
    setIsEditingDivision,
    editDivisionId,
    setEditDivisionId,
    formError,
    setFormError,
    emailError,
    setEmailError,
    departments,
    setDepartments,
    users,
    setUsers,
    roleIds,
    setRoleIds,

    // API Data
    academies,
    isLoadingAcademies,
    departmentData,
    refetchDepartments,
    isLoadingDepartments,
    usersData,
    isLoadingUsers,
    refetchUsers,
    roles,
    isLoadingRoles,
    divisions,
    refetchDivisions,
    isLoadingDivisions,

    // Computed Values
    academyId,
    filteredDivisionsForUser,
    departmentOptions,
    divisionOptions,

    // Mutations
    createUser,
    isCreatingUser,
    createDepartment,
    updateDepartment,
    isUpdatingDepartment,
    deleteDepartment,
    createDivision,
    updateDivision,
    isUpdatingDivision,
    deleteDivision,
    softDeleteUser,

    // Helper Functions
    getRoleIdForTab,
    showSuccessToast,
    showErrorAlert,
    showConfirmDialog,

    // Form Handlers
    resetUserForm,
    resetDepartmentForm,
    resetDivisionForm,
    handleUserFormChange,
    handleEmailChange,

    // Change Detection
    hasDepartmentChanges,
    hasUserChanges,
  };
};
